<script setup lang="ts">
import {computed, inject, ref, watch} from "vue";
import {useMedicationCheckupStore} from "@/medication-checkup/stores/medication-checkup-store";
import MedicationCheckupCopiloteLine from "@/medication-checkup/components/MedicationCheckupCopiloteLine.vue";
import MedicationCheckupUserEcho from "@/medication-checkup/components/MedicationCheckupUserEcho.vue";
import MedicationCheckupInterventionsCard from "@/medication-checkup/components/MedicationCheckupInterventionsCard.vue";
import MedicationCheckupInterventionsConfirmation from "@/medication-checkup/components/MedicationCheckupInterventionsConfirmation.vue";
import MedicationCheckupScanCard from "@/medication-checkup/components/MedicationCheckupScanCard.vue";
import MedicationCheckupCopiloteBubble from "@/medication-checkup/components/MedicationCheckupCopiloteBubble.vue";
import MedicationCheckupSceneReplies from "@/medication-checkup/components/MedicationCheckupSceneReplies.vue";
import {MedicationCheckupAction} from "@/medication-checkup/interfaces";
import {
    InterventionAddition,
    InterventionDraft,
    InterventionProduct,
    InterventionRow,
    InterventionsView
} from "@/medication-checkup/interfaces/interventions";
import {ACTION_DRAFT} from "@/medication-checkup/constants";
import {SceneProps, useScene} from "@/medication-checkup/composables/useScene";
import {useScanRun} from "@/medication-checkup/composables/useScanRun";
import {namesProduct, requiresOutcome} from "@/medication-checkup/services/InterventionStatuses";

/**
 * The scene of the pharmaceutical intervention: the before / after table
 * the pharmacist fills after the alerts, without the patient, and validates
 * as the proposal the summary carries to the doctor.
 *
 * Everything it shows is served by the handler: the rows with their matter,
 * the alerts on the profile, the statuses. Drafting is the scene's own
 * state — a draft per row, the additions — and the closing action submits
 * it whole.
 *
 * Landing on the live block asks the server for the AI draft, the way the
 * closing of the collection asks for its conclusion: the analysis lands on
 * every row and the suggestions are laid over the drafts, marked as the
 * AI's until the pharmacist touches them. Meanwhile the scan card tells
 * what the AI does, stage by stage as the server reports it, and the table
 * comes once the draft is revealed, so the seed never overwrites a word of
 * theirs.
 */

const props = defineProps<SceneProps<InterventionsView>>();

/**
 * The AI draft is asked for as the scene opens, so its wait overlaps the
 * typing of the bubble.
 */
const {
    bubbleShown,
    cardShown,
    clicked,
    view,
    text,
    actions,
    closing,
    acted,
    later,
    act: submitBlock
} = useScene(props, {
    card: true,
    live: () => {
        if (view.value?.draft.enabled && !view.value.draft.drafted) {
            draft();
        }
    }
});

const store = useMedicationCheckupStore();

const openConfirmationModal = inject('openConfirmationModal') as (
    title: string,
    message: string,
    success: () => Promise<void>
) => Promise<void>;

const submitting = ref(false);

/**
 * Whether this visit asks for the AI draft, so the card telling its work is
 * shown; a visit that finds the draft served shows the table at once.
 */
const asking = ref(false);

const {
    run,
    done: draftDone,
    failed: draftFailed,
    progress,
    revealStep,
    start
} = useScanRun({
    bubbleShown,
    later,
    stream: (report, signal) => store.draftBlock(props.block.id, report, signal),
    fallback: () => store.submitBlock(props.block.id, {action: ACTION_DRAFT})
});

/**
 * Whether the AI draft of the table is being awaited.
 */
const drafting = computed(() => asking.value && !draftDone.value && !draftFailed.value);

/**
 * Whether the table is shown: at once without a draft to wait for, once the
 * draft is revealed otherwise — or once it could not be had, the table then
 * filled by hand.
 */
const tableShown = computed(() => !asking.value || revealStep.value >= 1 || draftFailed.value);

/**
 * The closing action awaiting the last read of the changes, if any.
 */
const confirming = ref<MedicationCheckupAction | null>(null);

/**
 * What the pharmacist has to fill before validating, worded for them.
 */
const warning = ref<string | null>(null);

/**
 * The rows of the table: the retained treatments. While live, the served
 * rows also carry the additions the AI suggests, which are the scene's
 * additions and not rows of the table.
 */
const rows = computed(() => (view.value?.rows ?? []).filter(row => props.frozen || row.treatment_line_id !== null));

const blank = (): InterventionDraft => ({status: 'unchanged', product: null, outcome: '', comment: '', analysis: null});

const isBlank = (draft: InterventionDraft): boolean => {
    return draft.status === 'unchanged'
        && draft.product === null
        && draft.outcome === ''
        && draft.comment === ''
        && draft.analysis === null;
};

/**
 * The draft a served suggestion seeds: the AI's words, no comment, and the
 * analysis left as served.
 */
const seeded = (row: InterventionRow): InterventionDraft => ({
    status: row.intervention?.status ?? 'unchanged',
    product: row.intervention?.product ?? null,
    outcome: row.intervention?.outcome ?? '',
    comment: '',
    analysis: null
});

/**
 * A draft per served row — the AI's suggestion where there is one, every
 * other treatment left as it is — local until the closing action submits
 * them. Resets with the scene.
 */
const drafts = ref<Record<string, InterventionDraft>>(
    Object.fromEntries(rows.value.map(row => [row.key, seeded(row)]))
);

/**
 * The additions the AI suggests, served as rows without a treatment.
 */
const suggestedAdditions = (): InterventionAddition[] => {
    return (view.value?.rows ?? [])
        .filter(row => row.treatment_line_id === null && row.intervention?.product)
        .map(row => ({
            key: row.key,
            product: row.intervention!.product!,
            outcome: row.intervention?.outcome ?? '',
            comment: '',
            suggested: true,
            analysis: row.analysis
        }));
};

const additions = ref<InterventionAddition[]>(props.frozen ? [] : suggestedAdditions());

/**
 * Lay the draft over the table once it lands: the suggestions seed the
 * drafts still blank, the suggested additions join the scene's. The card
 * is busy while the draft is awaited, so a blank draft is an untouched one.
 */
watch(rows, () => {
    drafts.value = Object.fromEntries(rows.value.map(row => [
        row.key,
        drafts.value[row.key] === undefined || isBlank(drafts.value[row.key]) ? seeded(row) : drafts.value[row.key]
    ]));

    const known = additions.value.map(addition => addition.key);
    additions.value = [...additions.value, ...suggestedAdditions().filter(addition => !known.includes(addition.key))];
});

/**
 * Ask the server for the AI draft of the table, behind the card telling its
 * work.
 *
 * The cycle re-read once the draft is made carries it in the served view —
 * the watcher seeds the scene from it. A lost stream falls back on the plain
 * submission; a draft that could not be had leaves the table bare and the
 * pharmacist fills it by hand: the store has already reported why.
 */
const draft = (): Promise<void> => {
    asking.value = true;

    return start();
};

const draftOf = (row: InterventionRow): InterventionDraft => drafts.value[row.key] ?? blank();

const updateDraft = (key: string, draft: InterventionDraft) => {
    drafts.value = {...drafts.value, [key]: draft};
    warning.value = null;
};

const addProduct = (product: InterventionProduct, outcome: string) => {
    additions.value = [
        ...additions.value,
        {key: `added:${Date.now()}`, product, outcome, comment: ''}
    ];
    warning.value = null;
};

const updateAddition = (addition: InterventionAddition) => {
    additions.value = additions.value.map(current => current.key === addition.key ? addition : current);
    warning.value = null;
};

const removeAddition = (key: string) => {
    additions.value = additions.value.filter(addition => addition.key !== key);
};

/**
 * How many rows say something to the doctor: a change, an addition, or a
 * comment on a treatment left as it is.
 */
const proposals = computed(() => {
    return rows.value.filter(row => {
        const draft = draftOf(row);

        return draft.status !== 'unchanged' || draft.comment.trim() !== '';
    }).length + additions.value.length;
});

/**
 * The treatments whose change lacks its posology or its product, named so
 * the pharmacist knows where to go back to.
 */
const incomplete = computed<string[]>(() => {
    const lines = rows.value
        .filter(row => {
            const draft = draftOf(row);

            return (requiresOutcome(draft.status) && draft.outcome.trim() === '')
                || (namesProduct(draft.status) && draft.product === null);
        })
        .map(row => row.label);

    const added = additions.value
        .filter(addition => addition.outcome.trim() === '')
        .map(addition => addition.product.label);

    return [...lines, ...added];
});

const payload = () => ({
    interventions: [
        ...rows.value.map(row => {
            const draft = draftOf(row);

            return {
                treatment_line_id: row.treatment_line_id,
                status: draft.status,
                product_id: draft.product?.id ?? null,
                outcome: requiresOutcome(draft.status) ? (draft.outcome.trim() || null) : null,
                comment: draft.comment.trim() || null,
                analysis: draft.analysis === null ? (row.analysis ?? '') : draft.analysis.trim()
            };
        }),
        ...additions.value.map(addition => ({
            treatment_line_id: null,
            status: 'added',
            product_id: addition.product.id,
            outcome: addition.outcome.trim() || null,
            comment: addition.comment.trim() || null,
            analysis: addition.analysis ?? ''
        }))
    ]
});

/**
 * Submit the table whole.
 *
 * The echo lands with the click rather than with the answer — the act is the
 * pharmacist's, not the network's. If the submission fails, the quick replies
 * come back so the action can be retried; the store has already reported why.
 */
const submit = async (action: MedicationCheckupAction) => {
    submitting.value = true;

    try {
        await submitBlock(action, payload());
    } finally {
        submitting.value = false;
    }
};

/**
 * Take the closing action: refused while a change lacks its words, confirmed
 * on the changes themselves — read in before / after one last time — or, when
 * nothing is proposed at all, on the emptiness of the proposal.
 */
const act = async (action: MedicationCheckupAction) => {
    if (incomplete.value.length > 0) {
        warning.value = `Renseignez la proposition pour : ${incomplete.value.join(', ')}.`;

        return;
    }

    if (proposals.value === 0) {
        return openConfirmationModal(
            'Valider sans proposition ?',
            "Aucune modification, aucun ajout, aucune remarque : la synthèse n'aura rien à transmettre au médecin. "
            + 'Une fois validé, le tableau ne pourra plus être modifié.',
            () => submit(action)
        );
    }

    confirming.value = action;
};

/**
 * Submit what the recap showed, the dialog closing on the answer so a failed
 * submission is retried from the changes the pharmacist has under the eyes.
 */
const confirm = async () => {
    const action = confirming.value;

    if (action === null) {
        return;
    }

    await submit(action);

    confirming.value = null;
};
</script>

<template>
  <div class="flex flex-col gap-3.5">
    <template v-if="frozen">
      <MedicationCheckupCopiloteLine v-if="text" :text="text" />
      <MedicationCheckupInterventionsCard
        v-if="view"
        :rows="view.rows"
        :profile-alerts="view.profile_alerts"
        :statuses="view.statuses"
        frozen
      />
      <MedicationCheckupUserEcho v-if="acted" :text="acted" />
      <MedicationCheckupCopiloteLine v-if="closing" :text="closing" />
    </template>

    <template v-else>
      <MedicationCheckupCopiloteBubble
        :shown="bubbleShown"
        :text="text"
      />

      <MedicationCheckupScanCard
        v-if="asking && bubbleShown"
        title="L'IA Apodis prépare le tableau d'intervention"
        retry-label="La préparation a échoué — réessayer"
        :failed="draftFailed"
        :done="draftDone"
        :progress="progress"
        :run="run"
        class="animate-message-in"
        @retry="draft"
      />

      <MedicationCheckupInterventionsCard
        v-if="cardShown && view && tableShown"
        :rows="rows"
        :profile-alerts="view.profile_alerts"
        :statuses="view.statuses"
        :drafts="drafts"
        :additions="additions"
        :busy="submitting || drafting"
        :drafting="drafting"
        class="animate-message-in"
        @update:draft="updateDraft"
        @add="addProduct"
        @update:addition="updateAddition"
        @remove-addition="removeAddition"
      />

      <span
        v-if="warning"
        class="ml-10 text-xs font-semibold text-red-light animate-message-in"
      >
        {{ warning }}
      </span>

      <MedicationCheckupInterventionsConfirmation
        v-if="view"
        :open="confirming !== null"
        :rows="rows"
        :drafts="drafts"
        :additions="additions"
        :statuses="view.statuses"
        :busy="submitting"
        @update:open="confirming = $event ? confirming : null"
        @confirm="confirm"
      />

      <MedicationCheckupSceneReplies
        :clicked="clicked"
        :actions="actions"
        :shown="cardShown && tableShown"
        :disabled="drafting"
        @act="act"
      />
    </template>
  </div>
</template>
